// Shared contracts and pure logic for time tracking (tracker + timesheet). Owned by the time feature;
// consumed by reports and import.
import { z } from 'zod';
import { addDays } from './reports';

export const ENTRY_SOURCES = ['timer', 'manual', 'import'] as const;
export type EntrySource = (typeof ENTRY_SOURCES)[number];

/** Clockify allows very long entries; we cap a single entry at 7 days. */
export const MAX_ENTRY_SEC = 7 * 24 * 3600;
/** Longest value a timesheet cell accepts (one day). */
export const MAX_CELL_SEC = 24 * 3600;
/**
 * Most tracked time a day can hold (finished entries counted on their local start day). A Taim
 * rule, not Clockify's: writes that take a day past it fail with 400 `day_limit`.
 */
export const MAX_DAY_SEC = 24 * 3600;
export const DAY_LIMIT = 'day_limit';

/** The day_limit error message, e.g. "Mon, Sep 28 would total 25:00. A day can’t have …". */
export function dayLimitMessage(date: string, totalSec: number): string {
  const label = new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return `${label} would total ${formatHM(totalSec)}. A day can’t have more than 24 hours.`;
}

// ---------------------------------------------------------------------------------------------
// Contracts

/**
 * A time entry as returned by every /api/time-entries endpoint. `endAt`/`durationSec` are null
 * while the timer is running. Rates are hourly snapshots in minor units of the tenant currency.
 */
export const timeEntrySchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  projectId: z.uuid().nullable(),
  taskId: z.uuid().nullable(),
  description: z.string(),
  startAt: z.string(),
  endAt: z.string().nullable(),
  durationSec: z.number().int().nullable(),
  billable: z.boolean(),
  billRateCents: z.number().int().nullable(),
  costRateCents: z.number().int().nullable(),
  tagIds: z.array(z.uuid()),
  source: z.enum(ENTRY_SOURCES),
});
export type TimeEntry = z.infer<typeof timeEntrySchema>;

const instant = z.iso.datetime({ offset: true });
const description = z.string().max(3000);
const tagIds = z
  .array(z.uuid())
  .max(100)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Each tag can only be used once' });

const taskNeedsProject = <T extends { projectId?: string | null; taskId?: string | null }>(v: T) =>
  !v.taskId || !!v.projectId;
const taskNeedsProjectIssue = { message: 'A task needs a project', path: ['taskId'] };

const ms = (iso: string) => Date.parse(iso);
const endNotBeforeStart = (v: { startAt?: string; endAt?: string | null }) =>
  !v.startAt || !v.endAt || ms(v.endAt) >= ms(v.startAt);
const notTooLong = (v: { startAt?: string; endAt?: string | null }) =>
  !v.startAt || !v.endAt || ms(v.endAt) - ms(v.startAt) <= MAX_ENTRY_SEC * 1000;

const details = {
  description: description.default(''),
  projectId: z.uuid().nullish().default(null),
  taskId: z.uuid().nullish().default(null),
  tagIds: tagIds.default([]),
  /** Defaults to the project's billable setting (false without a project). */
  billable: z.boolean().optional(),
};

/** POST /api/time-entries/start. A running timer is stopped first (Clockify behaviour). */
export const timerStartInputSchema = z.object(details).refine(taskNeedsProject, taskNeedsProjectIssue);
export type TimerStartInput = z.input<typeof timerStartInputSchema>;

/** POST /api/time-entries/stop. `endAt` defaults to now. */
export const timerStopInputSchema = z.object({ endAt: instant.optional() });
export type TimerStopInput = z.input<typeof timerStopInputSchema>;

/** POST /api/time-entries: a manual entry. Admins may pass `userId` to add time for someone else. */
export const timeEntryInputSchema = z
  .object({ ...details, startAt: instant, endAt: instant, userId: z.uuid().optional() })
  .refine(taskNeedsProject, taskNeedsProjectIssue)
  .refine(endNotBeforeStart, { message: 'End must not be before start', path: ['endAt'] })
  .refine(notTooLong, { message: 'An entry can be at most 7 days long', path: ['endAt'] });
export type TimeEntryInput = z.input<typeof timeEntryInputSchema>;

/**
 * PATCH /api/time-entries/:id. Omitted fields are unchanged; `tagIds` replaces the tag list.
 * `userId` moves the entry to another user (admins only, like Clockify's Detailed report).
 * Unknown fields are rejected.
 */
export const timeEntryPatchSchema = z
  .strictObject({
    description: description.optional(),
    projectId: z.uuid().nullable().optional(),
    taskId: z.uuid().nullable().optional(),
    tagIds: tagIds.optional(),
    billable: z.boolean().optional(),
    startAt: instant.optional(),
    endAt: instant.optional(),
    userId: z.uuid().optional(),
  })
  .refine(endNotBeforeStart, { message: 'End must not be before start', path: ['endAt'] });
export type TimeEntryPatch = z.input<typeof timeEntryPatchSchema>;

const minuteOfDay = z
  .number()
  .int()
  .min(0)
  .max(24 * 60 - 1);

/**
 * Fields a bulk edit can set on every selected entry (Clockify's bulk edit dialog). Omitted
 * fields are unchanged. Times are read in the editor's time zone:
 *  - `startMinute` + `endMinute` (together): each entry's start–end time on its own day (an end
 *    before the start runs past midnight);
 *  - `date`: moves each entry to that day, keeping its time of day and duration (or, with a
 *    time, putting that time on that day).
 */
export const bulkSetSchema = z
  .object({
    description: description.optional(),
    projectId: z.uuid().nullable().optional(),
    taskId: z.uuid().nullable().optional(),
    tagIds: tagIds.optional(),
    /** 'replace' (default, Clockify "Overwrite existing") or 'add' ("Add to existing"). */
    tagMode: z.enum(['replace', 'add']).optional(),
    billable: z.boolean().optional(),
    /** Admins only: moves the entries to this user. */
    userId: z.uuid().optional(),
    date: z.iso.date().optional(),
    startMinute: minuteOfDay.optional(),
    endMinute: minuteOfDay.optional(),
  })
  .refine(
    (s) =>
      s.description !== undefined ||
      s.projectId !== undefined ||
      s.taskId !== undefined ||
      s.tagIds !== undefined ||
      s.billable !== undefined ||
      s.userId !== undefined ||
      s.date !== undefined ||
      s.startMinute !== undefined ||
      s.endMinute !== undefined,
    { message: 'Choose something to change' },
  )
  .refine((s) => (s.startMinute === undefined) === (s.endMinute === undefined), {
    message: 'Enter both a start and an end time',
    path: ['endMinute'],
  });
export type BulkSet = z.input<typeof bulkSetSchema>;

const bulkIds = z.array(z.uuid()).min(1, 'Select at least one entry').max(1000);

/** POST /api/time-entries/bulk: edit or delete several entries at once. */
export const bulkEntriesInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('delete'), ids: bulkIds }),
  z.object({ action: z.literal('update'), ids: bulkIds, set: bulkSetSchema }),
]);
export type BulkEntriesInput = z.input<typeof bulkEntriesInputSchema>;

/**
 * GET /api/time-entries?from=&to=&userId=. Entries starting in [from, to) newest first, plus the
 * running timer. Admins and managers may pass another user's id.
 */
export const timeEntryListQuerySchema = z.object({
  from: instant.optional(),
  to: instant.optional(),
  userId: z.uuid().optional(),
});
export type TimeEntryListQuery = z.input<typeof timeEntryListQuerySchema>;

/**
 * GET /api/time-entries/week-before?before=: the most recent week (tenant week start, user
 * timezone) that has entries starting before `before`. Drives the tracker's "Load more".
 */
export const weekBeforeQuerySchema = z.object({ before: instant, userId: z.uuid().optional() });
export type WeekPage = { weekStart: string | null; entries: TimeEntry[] };

const cell = z.number().int().min(0).max(MAX_CELL_SEC).nullable();

export const timesheetRowInputSchema = z
  .object({
    projectId: z.uuid().nullable(),
    taskId: z.uuid().nullable().default(null),
    /** Seconds per day, week order; null leaves that day untouched. */
    days: z.array(cell).length(7),
  })
  .refine(taskNeedsProject, taskNeedsProjectIssue);

/**
 * PUT /api/timesheet. Each non-null cell becomes the new total of that project/task on that day:
 * the difference is made up by adding to or trimming the day's manual entries (see the API).
 */
export const timesheetUpsertInputSchema = z.object({
  weekStart: z.iso.date(),
  userId: z.uuid().optional(),
  rows: z.array(timesheetRowInputSchema).max(200),
});
export type TimesheetUpsertInput = z.input<typeof timesheetUpsertInputSchema>;

export const timesheetQuerySchema = z.object({ weekStart: z.iso.date(), userId: z.uuid().optional() });

/** POST /api/timesheet/copy-last-week. */
export const copyLastWeekInputSchema = z.object({
  weekStart: z.iso.date(),
  /** Also copy last week's durations into this week's empty cells (Clockify "copy with time"). */
  withTime: z.boolean().default(false),
  userId: z.uuid().optional(),
});
export type CopyLastWeekInput = z.input<typeof copyLastWeekInputSchema>;

/**
 * POST /api/timesheet/remove-row (Clockify's row ✕): deletes all of the user's finished entries of
 * this project/task that start in the week, whatever their source. A running timer is kept.
 */
export const timesheetRemoveRowInputSchema = z
  .object({
    weekStart: z.iso.date(),
    userId: z.uuid().optional(),
    projectId: z.uuid().nullable(),
    taskId: z.uuid().nullable().default(null),
  })
  .refine(taskNeedsProject, taskNeedsProjectIssue);
export type TimesheetRemoveRowInput = z.input<typeof timesheetRemoveRowInputSchema>;

/**
 * POST /api/timesheet/change-row (Clockify: picking another project on a row): moves all of the
 * user's finished entries of this project/task that start in the week to `to`. They are checked
 * as any project change is (archived project, done task, the new client's tags and required
 * fields). A running timer is kept. The response is the week's timesheet.
 */
export const timesheetChangeRowInputSchema = z.object({
  weekStart: z.iso.date(),
  userId: z.uuid().optional(),
  from: z
    .object({ projectId: z.uuid().nullable(), taskId: z.uuid().nullable().default(null) })
    .refine(taskNeedsProject, taskNeedsProjectIssue),
  to: z
    .object({ projectId: z.uuid().nullable(), taskId: z.uuid().nullable().default(null) })
    .refine(taskNeedsProject, taskNeedsProjectIssue),
});
export type TimesheetChangeRowInput = z.input<typeof timesheetChangeRowInputSchema>;

// Timesheet templates (Clockify's "Save as template")

export const TEMPLATE_CONFLICTS = ['add', 'replace', 'skip'] as const;
export type TemplateConflict = (typeof TEMPLATE_CONFLICTS)[number];

const templateRow = z
  .object({
    projectId: z.uuid().nullable(),
    taskId: z.uuid().nullable().default(null),
    /** Seconds per day in week order; null when the template has no time. */
    days: z.array(z.number().int().min(0).max(MAX_CELL_SEC)).length(7).nullable().default(null),
  })
  .refine(taskNeedsProject, taskNeedsProjectIssue);

/** POST /api/timesheet-templates: saves the rows shown on a week (with their time, or not). */
export const timesheetTemplateInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  withTime: z.boolean().default(false),
  rows: z.array(templateRow).min(1, 'The timesheet has no rows to save').max(200),
});
export type TimesheetTemplateInput = z.input<typeof timesheetTemplateInputSchema>;

/** A saved template, as listed by GET /api/timesheet-templates (the caller's own only). */
export type TimesheetTemplate = {
  id: string;
  name: string;
  withTime: boolean;
  rows: Array<{ projectId: string | null; taskId: string | null; days: number[] | null }>;
  createdAt: string;
};

/**
 * POST /api/timesheet/apply-templates: adds the templates' rows to the week and, for templates
 * saved with time, their time. Where a cell already has time, `conflict` decides: add to it,
 * replace it, or skip the cell (Clockify's Add / Replace / Skip). The response is the week's
 * timesheet with the templates' rows (empty ones too) and `skippedCells` when some cells couldn't
 * be filled (a client requiring details, or time tracked with the timer that can't be replaced).
 */
export const applyTemplatesInputSchema = z.object({
  weekStart: z.iso.date(),
  userId: z.uuid().optional(),
  templateIds: z.array(z.uuid()).min(1).max(20),
  conflict: z.enum(TEMPLATE_CONFLICTS).default('skip'),
});
export type ApplyTemplatesInput = z.input<typeof applyTemplatesInputSchema>;

export type TimesheetRow = { projectId: string | null; taskId: string | null; days: number[] };
/** GET/PUT /api/timesheet response. `days` are the 7 local dates of the week. */
export type Timesheet = { weekStart: string; days: string[]; rows: TimesheetRow[] };

// ---------------------------------------------------------------------------------------------
// Durations

// hours (decimal allowed), then minutes (a bare number is only allowed last), then seconds.
const UNIT_RE =
  /^(?:(\d*[.,]?\d+)\s*(?:hours?|hrs?|h))?\s*(?:(\d+)\s*(?:(minutes?|mins?|m)|$))?\s*(?:(\d+)\s*(?:seconds?|secs?|s))?$/;

/**
 * Parses what people type into Clockify's duration fields (its default duration format), returning
 * seconds (or null): `1:30`, `1:30:15`, `:45`, `1.5` / `1,5` (hours), `90m`, `1h 30m`, `1h30`,
 * `30s`. Bare 1–2 digit numbers are minutes (`45` = 0:45, `99` = 1:39) and 3–4 digit numbers are
 * HMM/HHMM (`130` = 1:30). Minutes (and seconds) of 60 or more roll over (`1:61` = 2:01,
 * `1275` = 13:15), like Clockify.
 */
export function parseDuration(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;

  if (/^\d+$/.test(s)) {
    if (s.length <= 2) return Number(s) * 60;
    if (s.length > 4) return null;
    return Number(s.slice(0, -2)) * 3600 + Number(s.slice(-2)) * 60;
  }

  const colon = /^(\d*):(\d{1,2})(?::(\d{1,2}))?$/.exec(s);
  if (colon) {
    return Number(colon[1] || 0) * 3600 + Number(colon[2]) * 60 + Number(colon[3] ?? 0);
  }

  const decimal = /^(\d*)[.,](\d+)$/.exec(s);
  if (decimal) return Math.round(Number(`${decimal[1] || 0}.${decimal[2]}`) * 3600);

  const units = UNIT_RE.exec(s);
  if (units) {
    const [, h, m, mUnit, sec] = units;
    if (h === undefined && m === undefined && sec === undefined) return null;
    // A bare number after hours is minutes ("1h30"); otherwise minutes need their unit.
    if (m !== undefined && !mUnit && h === undefined) return null;
    const hours = h === undefined ? 0 : Number(h.replace(',', '.'));
    return Math.round(hours * 3600) + Number(m ?? 0) * 60 + Number(sec ?? 0);
  }
  return null;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

function split(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

/** `H:MM:SS`, e.g. 0:12:34 — entry durations and the tab title. */
export function formatEntryDuration(sec: number): string {
  const { h, m, s } = split(sec);
  return `${h}:${pad2(m)}:${pad2(s)}`;
}

/** `HH:MM:SS`, e.g. 00:12:34 — the tracker's running clock. */
export function formatClock(sec: number): string {
  const { h, m, s } = split(sec);
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

/** `H:MM` (seconds truncated) — timesheet cells and totals. */
export function formatHM(sec: number): string {
  const { h, m } = split(sec);
  return `${h}:${pad2(m)}`;
}

// ---------------------------------------------------------------------------------------------
// Time of day

/**
 * Parses a time of day into minutes after midnight: `9`, `930`, `0930`, `9:30`, `9.30`,
 * `21:30`, `9:30pm`, `9p`, `12am`. Returns null when invalid.
 */
export function parseTimeOfDay(input: string): number | null {
  const match = /^(\d{1,4})(?:[:.](\d{2}))?\s*(a|am|p|pm)?$/.exec(input.trim().toLowerCase());
  if (!match) return null;
  const [, digits, minutes, meridiem] = match as unknown as [string, string, string?, string?];
  let h: number;
  let m: number;
  if (minutes !== undefined) {
    if (digits.length > 2) return null;
    h = Number(digits);
    m = Number(minutes);
  } else if (digits.length <= 2) {
    h = Number(digits);
    m = 0;
  } else {
    h = Number(digits.slice(0, -2));
    m = Number(digits.slice(-2));
  }
  if (m > 59) return null;
  if (meridiem) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (meridiem.startsWith('p') ? 12 : 0);
  } else if (h > 23) {
    return null;
  }
  return h * 60 + m;
}

/** 24-hour `09:00`, `17:30` (AM/PM is still accepted when typed, see parseTimeOfDay). */
export function formatTimeOfDay(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = Math.floor(minutes % 60);
  return `${pad2(h)}:${pad2(m)}`;
}

// ---------------------------------------------------------------------------------------------
// Dates and time zones

const DAY_MS = 86_400_000;
const formatters = new Map<string, Intl.DateTimeFormat>();

function wallClock(at: number, timeZone: string) {
  let fmt = formatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, fmt);
  }
  const p: Record<string, number> = {};
  for (const part of fmt.formatToParts(new Date(at))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  return { y: p.year!, mo: p.month!, d: p.day!, h: p.hour!, mi: p.minute!, s: p.second! };
}

type InstantLike = string | number | Date;
const toMs = (at: InstantLike) => (at instanceof Date ? at.getTime() : typeof at === 'number' ? at : ms(at));

/** Offset of `timeZone` from UTC at an instant, in ms (e.g. +19_800_000 for Asia/Kolkata). */
function offsetMs(at: number, timeZone: string) {
  const w = wallClock(at, timeZone);
  return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - (at - (((at % 1000) + 1000) % 1000));
}

/**
 * The UTC instant of a wall-clock time (`date` at `minutes` after midnight) in a time zone.
 * Ambiguous times (DST fall-back) resolve to the earlier instant. Nonexistent times (DST
 * spring-forward gap) move forward by the gap, so 02:30 on a New York spring-forward day is 03:30
 * EDT and local midnight on a day whose DST starts at midnight is that day's first instant.
 */
export function zonedTimeToUtc(date: string, minutes: number, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, 0, minutes);
  // Offsets in force a day either side of the moment; at most one transition lies in between.
  const before = offsetMs(wall - DAY_MS, timeZone);
  const after = offsetMs(wall + DAY_MS, timeZone);
  const matches = [wall - before, wall - after].filter((t) => t + offsetMs(t, timeZone) === wall);
  if (matches.length > 0) return new Date(Math.min(...matches));
  // In a gap: read the wall time with the offset from before the jump, which lands after it.
  return new Date(wall - before);
}

/** Calendar date (YYYY-MM-DD) of an instant in a time zone. */
export function localDate(at: InstantLike, timeZone: string): string {
  const w = wallClock(toMs(at), timeZone);
  return `${w.y}-${pad2(w.mo)}-${pad2(w.d)}`;
}

/** Minutes after local midnight of an instant in a time zone. */
export function localMinutes(at: InstantLike, timeZone: string): number {
  const w = wallClock(toMs(at), timeZone);
  return w.h * 60 + w.mi;
}

/** Adds calendar days to a YYYY-MM-DD date. */
export type WeekRange = {
  /** First and last local dates of the week (inclusive). */
  start: string;
  end: string;
  days: string[];
  /** UTC instants of the week's start and of the next week's start. */
  from: string;
  to: string;
};

/**
 * The week containing `date` (a local YYYY-MM-DD date, or an instant read in `timeZone`),
 * starting on `weekStart` (0 = Sunday … 6 = Saturday).
 */
export function weekRange(date: string | Date, weekStart: number, timeZone: string): WeekRange {
  const day = typeof date === 'string' ? date : localDate(date, timeZone);
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  const start = addDays(day, -((dow - weekStart + 7) % 7));
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return {
    start,
    end: days[6]!,
    days,
    from: zonedTimeToUtc(start, 0, timeZone).toISOString(),
    to: zonedTimeToUtc(addDays(start, 7), 0, timeZone).toISOString(),
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon, Sep 29" (plus the year when it is not the current one). */
export function formatDayShort(date: string, today?: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const text = `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return today && today.slice(0, 4) !== date.slice(0, 4) ? `${text}, ${date.slice(0, 4)}` : text;
}

/** Clockify's day headers: "Today", "Yesterday", otherwise "Mon, Sep 29". */
export function dayLabel(date: string, today: string): string {
  if (date === today) return 'Today';
  if (date === addDays(today, -1)) return 'Yesterday';
  return formatDayShort(date, today);
}

// ---------------------------------------------------------------------------------------------
// Grouping

type Groupable = Pick<TimeEntry, 'startAt' | 'endAt' | 'durationSec'>;
const total = (entries: readonly Groupable[]) => entries.reduce((sum, e) => sum + (e.durationSec ?? 0), 0);

export type DayGroup<T> = { date: string; entries: T[]; totalSec: number };

/** When an entry was last active: its end, or now (later than any end) while it runs. */
const lastActive = (e: Groupable) => (e.endAt === null ? Number.POSITIVE_INFINITY : ms(e.endAt));

/**
 * Groups entries by the local day they start on (so an entry crossing midnight counts toward its
 * start day), newest day first. Within a day, the most recently active entry comes first: a
 * running one, then the latest end (newest start on ties). Running entries count as 0.
 */
export function groupByDay<T extends Groupable>(entries: readonly T[], timeZone: string): DayGroup<T>[] {
  const byDay = new Map<string, T[]>();
  for (const e of entries) {
    const day = localDate(e.startAt, timeZone);
    const list = byDay.get(day) ?? [];
    list.push(e);
    byDay.set(day, list);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, list]) => {
      const sorted = [...list].sort((a, b) => lastActive(b) - lastActive(a) || ms(b.startAt) - ms(a.startAt));
      return { date, entries: sorted, totalSec: total(sorted) };
    });
}

type Similar = Pick<TimeEntry, 'description' | 'projectId' | 'taskId' | 'billable' | 'tagIds'>;

/** Entries with the same key are shown as one collapsible row, like Clockify's grouped entries. */
export function similarKey(e: Similar): string {
  return JSON.stringify([e.description, e.projectId, e.taskId, e.billable, [...e.tagIds].sort()]);
}

export type SimilarGroup<T> = { key: string; entries: T[]; totalSec: number };

/** Groups similar entries, keeping the order in which each group first appears. */
export function groupSimilar<T extends Similar & Groupable>(entries: readonly T[]): SimilarGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const e of entries) {
    const key = similarKey(e);
    const list = groups.get(key) ?? [];
    list.push(e);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([key, list]) => ({ key, entries: list, totalSec: total(list) }));
}

// ---------------------------------------------------------------------------------------------
// Required fields (per client, Clockify's "Do not allow saving time without")

/** Error code when an entry lacks a field its client requires (400). `details.missing` lists them. */
export const DETAILS_REQUIRED = 'details_required';
/** Error code when the workspace requires a project and an entry has none (400). */
export const PROJECT_REQUIRED = 'project_required';
export const PROJECT_REQUIRED_MESSAGE = 'This workspace requires a project on every time entry.';
/** Error code when an entry's tags don't belong to its project's client (400). */
export const TAG_CLIENT_MISMATCH = 'tag_client_mismatch';

export type RequiredFields = { requireDescription: boolean; requireTags: boolean };
export type MissingField = 'description' | 'tags';

/** The fields an entry is missing for its client's requirements (empty = it may be saved). */
export function missingRequiredFields(
  required: RequiredFields | null,
  entry: { description: string; tagIds: readonly string[] },
): MissingField[] {
  const missing: MissingField[] = [];
  if (!required) return missing;
  if (required.requireDescription && entry.description.trim() === '') missing.push('description');
  if (required.requireTags && entry.tagIds.length === 0) missing.push('tags');
  return missing;
}

export function missingFieldsMessage(missing: readonly MissingField[]): string {
  const what = missing.map((m) => (m === 'tags' ? 'a tag' : 'a description')).join(' and ');
  return `This client requires ${what} on every time entry.`;
}
